import React, { useMemo, useRef, useState } from "react";
import { EmptyStateErro, EmptyStateSemPlanilha } from "../../components/EmptyState";
import FilterPanel from "../../components/FilterPanel";
import {
  BuscaInput,
  FilterField,
  FilterGrid,
  FilterLabel,
  FiltroSelect,
} from "../../components/PainelTabela/styles";
import BotaoExportacao from "../../components/Exportacao";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import { useDados } from "../../context/DadosContext";
import { isNo } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { getLeadTimeProducao } from "../../utils/marcenaria";
import { getTipoAmbienteSeguro } from "../../utils/tipoAmbiente";
import {
  Aba,
  Abas,
  Barra,
  BarraWrap,
  ClienteTabela,
  ClienteTabelaWrapper,
  DuasColunas,
  EmptyText,
  EtapaLabel,
  EtapaRow,
  EtapaStats,
  Secao,
  SecaoTitulo,
  StatMinMax,
  StatValor,
  Td,
  TdDestaque,
  Th,
  TipoQtd,
  Titulo,
  TopBar,
  TopBarActions,
  Wrapper,
} from "./styles";

const ETAPAS = [
  { label: "Liberação → Medição", ini: F.LIBERACAO_DA_OBRA, fim: F.MEDICAO_REALIZADO },
  { label: "Medição → Executivo", ini: F.MEDICAO_REALIZADO, fim: F.EXECUTIVO_REALIZADO },
  { label: "Executivo → Aprovação", ini: F.EXECUTIVO_REALIZADO, fim: F.APROVACAO_DO_EXECUTIVO },
  { label: "Aprovação → Otimizacao", ini: F.APROVACAO_DO_EXECUTIVO, fim: F.OTIMIZACAO_DO_LOTE },
  { label: "Otimizacao → Compra MDF", ini: F.OTIMIZACAO_DO_LOTE, fim: F.MDF_BORDO },
  { label: "Compra MDF → Corte", ini: F.MDF_BORDO, fim: F.DATA_DE_CORTE },
  { label: "Corte → Bordo", ini: F.DATA_DE_CORTE, fim: F.DATA_DE_BORDO },
  { label: "Bordo → Finalização Marc.", ini: F.DATA_DE_BORDO, fim: F.FINAL_MARCENARIA },
  { label: "Finalização → Carregamento", ini: F.FINAL_MARCENARIA, fim: F.CARREGAMENTO },
];

const INITIAL_FILTERS = {
  busca: "",
  tipo: "TODOS",
};

function getUniqueOptions(values) {
  return [...new Set(values.filter(Boolean).map((value) => String(value).trim()))].sort((left, right) =>
    left.localeCompare(right, "pt-BR", { numeric: true, sensitivity: "base" })
  );
}

function calcularDias(row, ini, fim) {
  if (!row[ini] || !row[fim]) {
    return null;
  }

  const dias = getLeadTimeProducao(row, row[ini], row[fim]);
  return dias >= 0 ? dias : null;
}

function stats(valores) {
  const validos = valores.filter((valor) => valor !== null);

  if (!validos.length) {
    return { media: null, min: null, max: null };
  }

  const media = (
    validos.reduce((accumulator, valor) => accumulator + valor, 0) / validos.length
  ).toFixed(1);

  return { media, min: Math.min(...validos), max: Math.max(...validos) };
}

export default function KpisTempos() {
  const { dados, erro, initialLoading } = useDados();
  const pageRef = useRef(null);
  const [abaAtiva, setAbaAtiva] = useState("etapas");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });

  const ativos = useMemo(
    () => dados.filter((row) => isNo(row[F.OBRA_FUTURA])),
    [dados]
  );
  const tipoOptions = useMemo(
    () => ["TODOS", ...getUniqueOptions(ativos.map((row) => getTipoAmbienteSeguro(row)))],
    [ativos]
  );
  const filtrados = useMemo(
    () =>
      ativos.filter((row) => {
        const tipo = getTipoAmbienteSeguro(row);
        const query = filters.busca.trim().toLowerCase();

        if (
          query &&
          ![row[F.NOME_DO_CLIENTE], row[F.AMBIENTE], tipo].join(" ").toLowerCase().includes(query)
        ) {
          return false;
        }

        if (filters.tipo !== "TODOS" && tipo !== filters.tipo) {
          return false;
        }

        return true;
      }),
    [ativos, filters]
  );

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.busca.trim()) chips.push(`Busca: ${filters.busca.trim()}`);
    if (filters.tipo !== "TODOS") chips.push(`Tipo: ${filters.tipo}`);
    return chips;
  }, [filters]);

  const analise = useMemo(() => {
    if (!dados.length) {
      return null;
    }

    const porEtapa = ETAPAS.map((etapa) => ({
      label: etapa.label,
      ...stats(filtrados.map((row) => calcularDias(row, etapa.ini, etapa.fim))),
    }));

    const leadTimes = filtrados
      .filter((row) => row[F.LIBERACAO_DA_OBRA] && row[F.CARREGAMENTO])
      .map((row) => ({
        cliente: row[F.NOME_DO_CLIENTE],
        ambiente: row[F.AMBIENTE],
        dias: calcularDias(row, F.LIBERACAO_DA_OBRA, F.CARREGAMENTO),
      }))
      .filter((row) => row.dias !== null)
      .sort((left, right) => left.dias - right.dias);

    const porTipo = {};
    filtrados
      .filter((row) => row[F.LIBERACAO_DA_OBRA] && row[F.CARREGAMENTO])
      .forEach((row) => {
        const tipo = getTipoAmbienteSeguro(row);
        const dias = calcularDias(row, F.LIBERACAO_DA_OBRA, F.CARREGAMENTO);

        if (dias === null) {
          return;
        }

        if (!porTipo[tipo]) {
          porTipo[tipo] = [];
        }

        porTipo[tipo].push(dias);
      });

    const mediaPorTipo = Object.entries(porTipo)
      .map(([tipo, valores]) => ({
        tipo,
        media: (valores.reduce((accumulator, valor) => accumulator + valor, 0) / valores.length).toFixed(1),
        min: Math.min(...valores),
        max: Math.max(...valores),
        qtd: valores.length,
      }))
      .sort((left, right) => Number(left.media) - Number(right.media));

    return {
      porEtapa,
      maisRapidos: leadTimes.slice(0, 5),
      maisDemorados: [...leadTimes].reverse().slice(0, 5),
      mediaPorTipo,
    };
  }, [dados, filtrados]);

  const exportSnapshotPayload = useMemo(() => {
    if (!analise) return null;
    return {
      titulo: "KPIs de Tempo",
      resumo: [
        { label: "Ambientes considerados", value: filtrados.length, type: "number" },
        { label: "Etapas analisadas",       value: analise.porEtapa.length, type: "number" },
      ],
      secoes: [
        { id: "etapas", label: "Tempo médio por Etapa",
          columns: [
            { key: "label",  label: "Etapa",  type: "text" },
            { key: "media",  label: "Média",  type: "days" },
            { key: "min",    label: "Mínimo", type: "days" },
            { key: "max",    label: "Máximo", type: "days" },
          ], rows: analise.porEtapa },
        { id: "rapidos", label: "Clientes mais rápidos",
          columns: [
            { key: "cliente",  label: "Cliente",  type: "text" },
            { key: "ambiente", label: "Ambiente", type: "text" },
            { key: "dias",     label: "Dias",     type: "days" },
          ], rows: analise.maisRapidos },
        { id: "lentos", label: "Clientes mais demorados",
          columns: [
            { key: "cliente",  label: "Cliente",  type: "text" },
            { key: "ambiente", label: "Ambiente", type: "text" },
            { key: "dias",     label: "Dias",     type: "days" },
          ], rows: analise.maisDemorados },
        { id: "tipos", label: "Tempo médio por Tipo de Ambiente",
          columns: [
            { key: "tipo",  label: "Tipo",  type: "text" },
            { key: "qtd",   label: "Qtd",   type: "number" },
            { key: "media", label: "Média", type: "days" },
            { key: "min",   label: "Mín.",  type: "days" },
            { key: "max",   label: "Máx.",  type: "days" },
          ], rows: analise.mediaPorTipo },
      ],
      visual: {
        ref: pageRef,
        sections: [
          { id: "etapas",   label: "Gráfico por Etapa",  selector: "[data-pdf='etapas']" },
          { id: "clientes", label: "Clientes",           selector: "[data-pdf='clientes']" },
          { id: "tipos",    label: "Por Tipo",           selector: "[data-pdf='tipos']" },
        ],
      },
    };
  }, [analise, filtrados.length]);
  useExportSnapshot("kpis-tempos", exportSnapshotPayload, pageRef);

  if (initialLoading) {
    return <PageLoader scope="kpisTempos" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  if (!analise) {
    return <EmptyStateSemPlanilha />;
  }

  const maxEtapa = Math.max(1, ...analise.porEtapa.map((etapa) => Number(etapa.media ?? 0)));
  const maxTipo = Math.max(1, ...analise.mediaPorTipo.map((tipo) => Number(tipo.media ?? 0)));

  const exportResumo = [
    { label: "Ambientes considerados", value: filtrados.length, type: "number" },
    { label: "Etapas analisadas",       value: analise.porEtapa.length, type: "number" },
  ];

  const exportSecoes = [
    {
      id: "etapas",
      label: "Tempo médio por Etapa",
      columns: [
        { key: "label",  label: "Etapa",  type: "text" },
        { key: "media",  label: "Média",  type: "days" },
        { key: "min",    label: "Mínimo", type: "days" },
        { key: "max",    label: "Máximo", type: "days" },
      ],
      rows: analise.porEtapa,
    },
    {
      id: "rapidos",
      label: "Clientes mais rápidos",
      columns: [
        { key: "cliente",  label: "Cliente",  type: "text" },
        { key: "ambiente", label: "Ambiente", type: "text" },
        { key: "dias",     label: "Dias",     type: "days" },
      ],
      rows: analise.maisRapidos,
    },
    {
      id: "lentos",
      label: "Clientes mais demorados",
      columns: [
        { key: "cliente",  label: "Cliente",  type: "text" },
        { key: "ambiente", label: "Ambiente", type: "text" },
        { key: "dias",     label: "Dias",     type: "days" },
      ],
      rows: analise.maisDemorados,
    },
    {
      id: "tipos",
      label: "Tempo médio por Tipo de Ambiente",
      columns: [
        { key: "tipo",  label: "Tipo",  type: "text" },
        { key: "qtd",   label: "Qtd",   type: "number" },
        { key: "media", label: "Média", type: "days" },
        { key: "min",   label: "Mín.",  type: "days" },
        { key: "max",   label: "Máx.",  type: "days" },
      ],
      rows: analise.mediaPorTipo,
    },
  ];

  const exportVisual = {
    ref: pageRef,
    sections: [
      { id: "etapas",   label: "Gráfico por Etapa",  selector: "[data-pdf='etapas']" },
      { id: "clientes", label: "Clientes",           selector: "[data-pdf='clientes']" },
      { id: "tipos",    label: "Por Tipo",           selector: "[data-pdf='tipos']" },
    ],
  };

  return (
    <Wrapper ref={pageRef}>
      <TopBar>
        <Titulo>KPIs de Tempo</Titulo>
        <TopBarActions>
          <BuscaInput
            type="search"
            aria-label="Busca geral nos KPIs de tempo"
            value={filters.busca}
            onChange={(event) => setFilters((current) => ({ ...current, busca: event.target.value }))}
            placeholder="Cliente, ambiente, tipo..."
          />
          <FilterPanel
            compact
            title="Filtros de KPIs de tempo"
            meta={`${filtrados.length} de ${ativos.length} ambientes ativos considerados`}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((current) => !current)}
            onClose={() => setFiltersOpen(false)}
            onReset={() => setFilters({ ...INITIAL_FILTERS })}
            chips={activeFilterChips}
          >
            <FilterGrid>
              <FilterField>
                <FilterLabel>Tipo de ambiente</FilterLabel>
                <FiltroSelect
                  value={filters.tipo}
                  onChange={(event) => setFilters((current) => ({ ...current, tipo: event.target.value }))}
                >
                  {tipoOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "TODOS" ? "Todos" : option}
                    </option>
                  ))}
                </FiltroSelect>
              </FilterField>
            </FilterGrid>
          </FilterPanel>
          <BotaoExportacao
            titulo="KPIs de Tempo"
            resumo={exportResumo}
            secoes={exportSecoes}
            visual={exportVisual}
            filtrosAtivos={activeFilterChips}
          />
        </TopBarActions>
      </TopBar>

      <Abas>
        {[
          { id: "etapas", label: "Por Etapa" },
          { id: "clientes", label: "Por Cliente" },
          { id: "tipos", label: "Por Tipo de Ambiente" },
        ].map((aba) => (
          <Aba key={aba.id} $active={abaAtiva === aba.id} onClick={() => setAbaAtiva(aba.id)}>
            {aba.label}
          </Aba>
        ))}
      </Abas>

      {abaAtiva === "etapas" && (
        <Secao data-pdf="etapas">
          <SecaoTitulo>Tempo medio por etapa do fluxo (dias produtivos sem bloqueio)</SecaoTitulo>
          {analise.porEtapa.map((etapa, index) => (
            <EtapaRow
              key={etapa.label}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <EtapaLabel>{etapa.label}</EtapaLabel>
              <BarraWrap>
                <Barra
                  $width={etapa.media ? `${(Number(etapa.media) / maxEtapa) * 100}%` : "0%"}
                />
              </BarraWrap>
              <EtapaStats>
                <StatValor>{etapa.media != null ? `${etapa.media} du` : "—"}</StatValor>
                <StatMinMax>
                  {etapa.min != null && etapa.max != null ? `min ${etapa.min} / max ${etapa.max}` : ""}
                </StatMinMax>
              </EtapaStats>
            </EtapaRow>
          ))}
        </Secao>
      )}

      {abaAtiva === "clientes" && (
        <Secao data-pdf="clientes">
          <DuasColunas>
            <div>
              <SecaoTitulo>5 clientes mais rapidos</SecaoTitulo>
              <ClienteTabelaWrapper>
                <ClienteTabela>
                  <thead>
                    <tr>
                      <Th>#</Th>
                      <Th>Cliente</Th>
                      <Th>Ambiente</Th>
                      <Th>Dias uteis</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {analise.maisRapidos.map((row, index) => (
                      <tr key={`${row.cliente}-${row.ambiente}-${index}`}>
                        <Td>{index + 1}</Td>
                        <Td>{row.cliente}</Td>
                        <Td>{row.ambiente}</Td>
                        <TdDestaque $tone="success">{row.dias} du</TdDestaque>
                      </tr>
                    ))}
                    {analise.maisRapidos.length === 0 && (
                      <tr>
                        <Td colSpan={4}>Sem dados suficientes</Td>
                      </tr>
                    )}
                  </tbody>
                </ClienteTabela>
              </ClienteTabelaWrapper>
            </div>

            <div>
              <SecaoTitulo>5 clientes mais demorados</SecaoTitulo>
              <ClienteTabelaWrapper>
                <ClienteTabela>
                  <thead>
                    <tr>
                      <Th>#</Th>
                      <Th>Cliente</Th>
                      <Th>Ambiente</Th>
                      <Th>Dias uteis</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {analise.maisDemorados.map((row, index) => (
                      <tr key={`${row.cliente}-${row.ambiente}-slow-${index}`}>
                        <Td>{index + 1}</Td>
                        <Td>{row.cliente}</Td>
                        <Td>{row.ambiente}</Td>
                        <TdDestaque $tone="danger">{row.dias} du</TdDestaque>
                      </tr>
                    ))}
                    {analise.maisDemorados.length === 0 && (
                      <tr>
                        <Td colSpan={4}>Sem dados suficientes</Td>
                      </tr>
                    )}
                  </tbody>
                </ClienteTabela>
              </ClienteTabelaWrapper>
            </div>
          </DuasColunas>
        </Secao>
      )}

      {abaAtiva === "tipos" && (
        <Secao data-pdf="tipos">
          <SecaoTitulo>Tempo medio por tipo de ambiente (Liberacao → Carregamento, sem bloqueio)</SecaoTitulo>
          {analise.mediaPorTipo.length === 0 && <EmptyText>Sem dados suficientes ainda.</EmptyText>}
          {analise.mediaPorTipo.map((tipo, index) => (
            <EtapaRow
              key={tipo.tipo}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <EtapaLabel>
                {tipo.tipo} <TipoQtd>({tipo.qtd} amb.)</TipoQtd>
              </EtapaLabel>
              <BarraWrap>
                <Barra $width={`${(Number(tipo.media) / maxTipo) * 100}%`} />
              </BarraWrap>
              <EtapaStats>
                <StatValor>{tipo.media} du</StatValor>
                <StatMinMax>
                  min {tipo.min} / max {tipo.max}
                </StatMinMax>
              </EtapaStats>
            </EtapaRow>
          ))}
        </Secao>
      )}
    </Wrapper>
  );
}
